import { useForm, usePage } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { TagCombobox } from '@/components/TagCombobox'
import SuggestCombobox from '@/components/admin/research/SuggestCombobox'
import { US_STATES } from '@/lib/usStates'
import type { ResearchOrganizationDetail } from '@/types/research'

type Props = {
  organization?: ResearchOrganizationDetail
  causes: string[]
}

export default function ResearchOrganizationForm({ organization, causes }: Props) {
  const { data, setData, post, patch, processing, errors } = useForm({
    research_organization: {
      name: organization?.name ?? '',
      website: organization?.website ?? '',
      budget: organization?.budget ?? '',
      city: organization?.city ?? '',
      state: organization?.state ?? '',
      about: organization?.about ?? '',
      causes: organization?.causes ?? [],
    },
  })

  const org = data.research_organization
  const pageErrors = usePage().props.errors as Record<string, string | string[]> | undefined

  function setField<K extends keyof typeof org>(key: K, value: (typeof org)[K]) {
    setData('research_organization', { ...org, [key]: value })
  }

  function fieldError(field: string): string | undefined {
    const formErr = errors[`research_organization.${field}` as keyof typeof errors] as
      | string
      | undefined
    if (formErr) return formErr
    const pageErr = pageErrors?.[field]
    return Array.isArray(pageErr) ? pageErr[0] : pageErr
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (organization) {
      patch(`/admin/research/organizations/${organization.id}`)
    } else {
      post('/admin/research/organizations')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization details</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">
                Name<span className="text-destructive"> *</span>
              </Label>
              <Input id="org-name" value={org.name} onChange={e => setField('name', e.target.value)} />
              {fieldError('name') && <p className="text-xs text-destructive">{fieldError('name')}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-website">Website</Label>
              <Input
                id="org-website"
                type="url"
                placeholder="https://..."
                value={org.website}
                onChange={e => setField('website', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="org-budget">Budget</Label>
              <Input
                id="org-budget"
                placeholder="e.g. $1M–$5M"
                value={org.budget}
                onChange={e => setField('budget', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-city">City</Label>
              <Input id="org-city" value={org.city} onChange={e => setField('city', e.target.value)} />
            </div>
            <SuggestCombobox
              label="State"
              id="org-state"
              suggestions={US_STATES}
              value={org.state}
              onChange={value => setField('state', value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="org-about">About</Label>
            <Textarea
              id="org-about"
              rows={4}
              value={org.about}
              onChange={e => setField('about', e.target.value)}
            />
          </div>

          <TagCombobox
            label="Causes"
            placeholder="Type to search causes..."
            suggestions={causes}
            selected={org.causes}
            onChange={items => setField('causes', items)}
            allowCustom
          />

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={processing}>
              {organization ? 'Save changes' : 'Create organization'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
